import { config } from '@/config/env';

export interface SupplierLoginRequest {
  email: string;
  password: string;
}

export interface SupplierLoginResponse {
  access_token: string;
  supplier: {
    id: string;
    rfc: string;
    legalName: string;
    mainContactName?: string;
    status: string;
  };
}

export interface SupplierRegisterRequest {
  rfc: string;
  email: string;
  password: string;
  acceptTerms: boolean;
  // Optional fields
  legalName?: string;
  personType?: string;
  serviceTypeId?: string;
  mainContactPhone?: string;
  vatType?: string;
  mainContactName?: string;
  salesEmail?: string;
  accountsPayableEmail?: string;
  internalRequesterName?: string;
  country?: string;
  state?: string;
  city?: string;
  municipality?: string;
  suburb?: string;
  postalCode?: string;
  street?: string;
  externalNumber?: string;
  internalNumber?: string;
  repseNumber?: string;
}

export interface SupplierRegisterResponse {
  message: string;
  supplierId: string;
  status: string;
}

class SupplierAuthService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.apiBaseUrl;
  }

  async login(credentials: SupplierLoginRequest): Promise<SupplierLoginResponse> {
    const response = await fetch(`${this.baseUrl}/suppliers/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Error al iniciar sesión');
    }

    return await response.json();
  }

  async register(data: SupplierRegisterRequest): Promise<SupplierRegisterResponse> {
    const response = await fetch(`${this.baseUrl}/suppliers/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Error al registrar proveedor');
    }

    return await response.json();
  }

  saveToken(token: string): void {
    localStorage.setItem('supplier_access_token', token);
  }

  saveSupplierData(supplier: SupplierLoginResponse['supplier']): void {
    localStorage.setItem('supplier_data', JSON.stringify(supplier));
  }

  getToken(): string | null {
    return localStorage.getItem('supplier_access_token');
  }

  getSupplierData(): SupplierLoginResponse['supplier'] | null {
    const data = localStorage.getItem('supplier_data');
    return data ? JSON.parse(data) : null;
  }

  removeToken(): void {
    localStorage.removeItem('supplier_access_token');
    localStorage.removeItem('supplier_data');
  }
}

export const supplierAuthService = new SupplierAuthService();
